import React, { useState } from "react";
import { Loader2, Plus, Pencil, Zap } from "lucide-react";
import {
  AutotradeTrigger,
  AutotradeTriggerCategory,
  TriggerPriceAlertType,
  TriggerSource,
} from "@crypto-trading/common/browser";
import { useUserTriggers } from "@/hooks/useAutotradeTriggers";
import { AutotradeTriggerForm } from "./AutotradeTriggerForm";
import { useUserExchanges } from "@/hooks/useExchanges";

const formatExchangeName = (code: string) =>
  code.charAt(0).toUpperCase() + code.slice(1);

const formatTypeLabel = (type: string) =>
  type.charAt(0).toUpperCase() + type.slice(1);

// ─── Props ─────────────────────────────────────────────────────────────────────

interface AutotradeTriggerManagerProps {
  userId: string;
}

// ─── View state ────────────────────────────────────────────────────────────────

type ViewState =
  | { mode: "list" }
  | { mode: "create" }
  | { mode: "edit"; trigger: AutotradeTrigger };

// ─── Component ────────────────────────────────────────────────────────────────

export const AutotradeTriggerManager: React.FC<
  AutotradeTriggerManagerProps
> = ({ userId }) => {
  const [view, setView] = useState<ViewState>({ mode: "list" });

  const {
    data: triggers = [],
    isLoading,
    isError,
    error,
  } = useUserTriggers(userId);
  const { data: userExchanges = [], isLoading: isLoadingExchanges } =
    useUserExchanges(userId);

  const handleFormSaved = () => setView({ mode: "list" });

  if (isLoadingExchanges) {
    return (
      <div className="glass !rounded-2xl p-8 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent mx-auto mb-4" />
        <p className="text-text-primary">Loading…</p>
      </div>
    );
  }

  if (userExchanges.length === 0) {
    return (
      <div className="glass !rounded-2xl flex flex-col-reverse items-center gap-3 p-4 text-center lg:flex-row lg:items-center lg:text-left">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-xs font-semibold uppercase leading-[18px] text-[#00c27e]">
            Autotrade Settings
          </p>
          <h2 className="text-base font-semibold leading-6 text-white">
            Connect an Exchange First
          </h2>
          <p className="text-sm leading-5 text-[#a3a3a3]">
            To enable autotrading, connect at least one exchange account. Then
            set up your triggers and Trigger trades listings for you.
          </p>
        </div>
        <img
          src="/autotrade-connect.png"
          alt=""
          className="h-[130px] w-[166px] shrink-0 object-contain"
        />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-white/[0.12] bg-[#0d121a]/80 p-4 space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase leading-[18px] text-[#00c27e]">
            Autotrade Triggers
          </p>
          {view.mode === "list" && (
            <p className="text-xs text-text-secondary">
              Rules that open positions on listing news or price spikes/drops
            </p>
          )}
          {view.mode === "create" && (
            <p className="text-xs text-text-secondary">New trigger</p>
          )}
          {view.mode === "edit" && (
            <p className="text-xs text-text-secondary">Edit trigger</p>
          )}
        </div>
        {view.mode === "list" ? (
          <button
            type="button"
            onClick={() => setView({ mode: "create" })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-700 text-xs font-semibold text-white hover:bg-white/5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            New
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setView({ mode: "list" })}
            className="px-3 py-1.5 rounded-lg border border-gray-700 text-xs font-semibold text-text-secondary hover:text-white hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
        )}
      </div>

      {/* Create / Edit form */}
      {(view.mode === "create" || view.mode === "edit") && (
        <AutotradeTriggerForm
          userId={userId}
          trigger={view.mode === "edit" ? view.trigger : undefined}
          existingTriggers={triggers}
          onSaved={handleFormSaved}
        />
      )}

      {/* List view */}
      {view.mode === "list" && (
        <>
          {isLoading && (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-accent" />
            </div>
          )}

          {isError && (
            <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              {error instanceof Error
                ? error.message
                : "Failed to load triggers"}
            </div>
          )}

          {!isLoading && !isError && triggers.length === 0 && (
            <div className="text-center py-8 space-y-3">
              <p className="text-text-secondary text-sm">
                No triggers set up yet.
              </p>
              <button
                type="button"
                onClick={() => setView({ mode: "create" })}
                className="px-4 py-2 rounded-xl bg-accent text-white text-sm font-semibold hover:bg-accent/80"
              >
                Create your first trigger
              </button>
            </div>
          )}

          {triggers.length > 0 && (
            <div className="space-y-3">
              {triggers.map((trigger) => (
                <TriggerCard
                  key={trigger.id}
                  trigger={trigger}
                  onEdit={() => setView({ mode: "edit", trigger })}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── Trigger card ──────────────────────────────────────────────────────────────

interface TriggerCardProps {
  trigger: AutotradeTrigger;
  onEdit: () => void;
}

const TriggerCard: React.FC<TriggerCardProps> = ({ trigger, onEdit }) => {
  const isPrice =
    trigger.triggerSource === TriggerSource.PRICE_ALERT;

  return (
    <div className="rounded-2xl border border-gray-800 bg-black/30 px-4 py-3 flex items-center gap-3">
      {/* Status dot */}
      <span
        className={`w-2 h-2 rounded-full flex-shrink-0 ${trigger.isActive ? "bg-green-400" : "bg-gray-600"}`}
      />

      {/* Description */}
      <p className="flex-1 text-sm text-text-primary leading-relaxed">
        {isPrice ? (
          <>
            When price{" "}
            <span className="text-[#4F6EFF] font-medium">
              {trigger.priceAlertType === TriggerPriceAlertType.SPIKE
                ? "spikes"
                : "drops"}
            </span>{" "}
            by at least {trigger.priceChangeThreshold ?? "?"}% within{" "}
            {trigger.priceTimeframe ?? "?"}
            {trigger.tradingPair
              ? ` (${trigger.tradingPair})`
              : ""}{" "}
            on{" "}
            <span className="text-accent font-medium">
              {trigger.exchanges
                .map(
                  (e) =>
                    `${formatExchangeName(e.exchangeCode)} - ${formatTypeLabel(e.exchangeType)}`
                )
                .join(", ")}
            </span>
            , go{" "}
            <span
              className={
                "font-medium" +
                (trigger.hotkey.positionType === "LONG"
                  ? " text-green-500"
                  : " text-red-500")
              }
            >
              {trigger.hotkey.positionType === "LONG" ? "long" : "short"}
            </span>{" "}
            with {trigger.hotkey.amount} USDT, leverage{" "}
            {trigger.hotkey.leverage}x
            {trigger.hotkey.takeProfitPercent
              ? `, TP ${trigger.hotkey.takeProfitPercent}%`
              : ""}
            {trigger.hotkey.stopLossPercent
              ? `, SL ${trigger.hotkey.stopLossPercent}%`
              : ""}
            .
          </>
        ) : (
          <>
            When a token{" "}
            <span className="text-[#4F6EFF] font-medium">
              {trigger.category === AutotradeTriggerCategory.LISTING
                ? "lists"
                : "delists"}{" "}
            </span>
            on{" "}
            <span className="text-accent font-medium">
              {trigger.exchanges
                .map(
                  (e) =>
                    `${formatExchangeName(e.exchangeCode)} - ${formatTypeLabel(e.exchangeType)}`
                )
                .join(", ")}
            </span>
            , go{" "}
            <span
              className={
                "font-medium" +
                (trigger.hotkey.positionType === "LONG"
                  ? " text-green-500"
                  : " text-red-500")
              }
            >
              {trigger.hotkey.positionType === "LONG" ? "long" : "short"}
            </span>{" "}
            with {trigger.hotkey.amount} USDT, leverage{" "}
            {trigger.hotkey.leverage}x
            {trigger.hotkey.takeProfitPercent
              ? `, TP ${trigger.hotkey.takeProfitPercent}%`
              : ""}
            {trigger.hotkey.stopLossPercent
              ? `, SL ${trigger.hotkey.stopLossPercent}%`
              : ""}
            {trigger.openOnAllExchanges ? (
              <>
                {" "}
                on{" "}
                <span className="font-medium text-amber-400">
                  <span className="whitespace-nowrap">
                    <Zap className="mr-0.5 inline h-3 w-3 align-[-1px]" />
                    every
                  </span>{" "}
                  connected exchange at once
                </span>
              </>
            ) : null}
            .
          </>
        )}
      </p>

      {/* Edit button */}
      <button
        type="button"
        onClick={onEdit}
        className="flex-shrink-0 p-1.5 rounded-lg text-text-secondary hover:text-white hover:bg-white/5 transition-colors"
        aria-label="Edit trigger"
      >
        <Pencil className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};
